import { max } from "lodash";
import { Decimal, sumDecimalWithFunction } from "../decimal";
import { DatabaseId } from "../types/helpers/Id";
import { PopulatedAssetHolding } from "../types/models/AssetHoldingBase";
import { DataSourceType } from "../types/models/DataSourceBase";
import {
  SyncState,
  getWalletName,
  WalletImportMethod,
  WalletData,
} from "../types/models/WalletBase";
import { DataSourceData } from "../types/responses/TransactionEvent";
import { AssetValueService } from "./assets/AssetValueService";

export type HoldingDetails = {
  wallet: {
    id: DatabaseId;
    name: string;
    dataSource?: string; // name
    address?: string;
    image?: string;
    updatedAt?: Date;
    marketValue?: Decimal;
    canSync?: boolean;
    syncState?: SyncState;
  };
  holding?: PopulatedAssetHolding;
};

export function getHoldingsTableData({
  walletHoldings,
  valueService,
  byAsset,
}: {
  walletHoldings: {
    wallets: (WalletData & {
      _id: DatabaseId;
      dataSource: DataSourceData;
      holdings?: PopulatedAssetHolding[];
    })[];
    accountHoldings: PopulatedAssetHolding[];
    missingHoldings: PopulatedAssetHolding[];
  };
  valueService?: AssetValueService;
  byAsset: boolean;
}): HoldingDetails[] {
  return (
    walletHoldings.wallets
      // wallet holdings
      .flatMap((wallet) => {
        const walletDetails: HoldingDetails["wallet"] = {
          id: wallet._id,
          name: getWalletName({ wallet, dataSource: wallet.dataSource }),
          dataSource: wallet.dataSource.name,
          canSync: wallet.importMethod === WalletImportMethod.API,
          syncState: wallet.syncState,
          address:
            (
              wallet.dataSource.type === DataSourceType.Blockchain ||
              wallet.dataSource.type === DataSourceType.Wallet
            ) ?
              wallet.apiKey
            : undefined,
          image: wallet.dataSource.image,
          updatedAt: max([wallet.lastSynced, wallet.lastUploaded]),
          marketValue:
            valueService &&
            wallet.holdings &&
            sumDecimalWithFunction(
              wallet.holdings.map((holding) =>
                valueService.getValue({
                  asset: holding.asset,
                  amount: holding.balance,
                }),
              ),
              (value: Decimal | undefined) => value,
            ),
        };

        if (!wallet.holdings || !wallet.holdings.length)
          return {
            wallet: walletDetails,
            holding: undefined,
          } as HoldingDetails;

        return wallet.holdings.flatMap((holding) => ({
          wallet: walletDetails,
          holding: {
            ...holding,
            marketValue: valueService?.getValue({
              asset: holding.asset,
              amount: holding.balance,
            }),
          },
        }));
      })
      // missing holdings
      .concat(
        walletHoldings.missingHoldings?.length ?
          walletHoldings.missingHoldings.flatMap((holding) => ({
            wallet: {
              id: "missing",
              name: "Missing Balances",
              image: "/cloud.png",
              updatedAt: max(
                walletHoldings.wallets.map((wallet) =>
                  max([wallet.lastSynced, wallet.lastUploaded]),
                ),
              ),
              marketValue:
                valueService &&
                sumDecimalWithFunction(
                  walletHoldings.missingHoldings.map((holding) =>
                    valueService.getValue({
                      asset: holding.asset,
                      amount: holding.balance,
                    }),
                  ),
                  (value) => value?.abs(),
                ),
            },
            holding: {
              ...holding,
              marketValue: valueService?.getValue({
                asset: holding.asset,
                amount: holding.balance,
              }),
            },
          }))
        : [],
      )
      // account holdings (total portfolio)
      .concat(
        byAsset ? []
        : walletHoldings.accountHoldings.length ?
          walletHoldings.accountHoldings.flatMap((holding) => ({
            wallet: {
              id: "total",
              name: "Total Portfolio",
              image: "/syla-custom.png",
              updatedAt: max(
                walletHoldings.wallets.map((wallet) =>
                  max([wallet.lastSynced, wallet.lastUploaded]),
                ),
              ),
              marketValue:
                valueService &&
                sumDecimalWithFunction(
                  walletHoldings.accountHoldings.map((holding) =>
                    valueService.getValue({
                      asset: holding.asset,
                      amount: holding.balance,
                    }),
                  ),
                  (value) => value,
                ),
            },
            holding: {
              ...holding,
              marketValue: valueService?.getValue({
                asset: holding.asset,
                amount: holding.balance,
              }),
            },
          }))
        : [
            {
              wallet: {
                id: "0",
                name: "Total Portfolio",
                image: "/android-chrome-192x192.png",
              },
              holding: undefined,
            } as HoldingDetails,
          ],
      )
      .filter((holding) => !byAsset || !!holding.holding)
  );
}

type PortfolioAssetHoldings = {
  wallets: { holdings?: PopulatedAssetHolding[] }[];
  accountHoldings: PopulatedAssetHolding[];
  missingHoldings: PopulatedAssetHolding[];
};

export function aggregateWalletHoldingAssets({
  wallets,
  accountHoldings,
  missingHoldings,
}: PortfolioAssetHoldings) {
  return wallets
    .flatMap((wallet) => wallet.holdings?.map((h) => h.asset) ?? [])
    .concat(accountHoldings.map((holding) => holding.asset))
    .concat(missingHoldings.map((holding) => holding.asset));
}
